import { type CalendarDate, getLocalTimeZone } from '@internationalized/date';

export interface RangeToTimestampsOptions {
  /** Max span in days; longer ranges clamp the start forward. Default 365. */
  maxDays?: number;
  /** Timezone for the day boundaries. Defaults to the viewer's local zone (Ember parity). */
  timeZone?: string;
}

/** `CalendarDate` → unix seconds at 00:00:00 in `timeZone`. */
function startOfDayUnix(date: CalendarDate, timeZone: string): number {
  return Math.floor(date.toDate(timeZone).getTime() / 1000);
}

/** `CalendarDate` → unix seconds at 23:59:59 in `timeZone`. */
function endOfDayUnix(date: CalendarDate, timeZone: string): number {
  return startOfDayUnix(date.add({ days: 1 }), timeZone) - 1;
}

/**
 * A selected calendar range → the deliveries query's inclusive unix-second bounds:
 * start-of-day for the start, end-of-day (23:59:59) for the end, in the viewer's
 * **local** timezone — matching Ember (`moment().startOf/endOf('day')`). A range
 * ending today therefore ends at local midnight tonight (a future bound), so the
 * list stays live under the 60s auto-refresh and isn't truncated for viewers west
 * of UTC. The span clamps to `maxDays` (the query rejects ranges > 365 days), by **calendar
 * days** so a DST transition inside the span doesn't shift the clamped start by an hour.
 */
export function rangeToTimestamps(
  range: { start: CalendarDate; end: CalendarDate },
  { maxDays = 365, timeZone = getLocalTimeZone() }: RangeToTimestampsOptions = {},
): { startTs: number; endTs: number } {
  // Clamp to `maxDays` inclusive calendar days: since the end is inclusive (extended to 23:59:59),
  // the earliest allowed start is `maxDays - 1` days before it, keeping the span at most `maxDays`
  // days (never the `maxDays + 1` an end-of-day bound would otherwise produce).
  const maxStart = range.end.subtract({ days: maxDays - 1 });
  const start = range.start.compare(maxStart) < 0 ? maxStart : range.start;
  return { startTs: startOfDayUnix(start, timeZone), endTs: endOfDayUnix(range.end, timeZone) };
}
